import React, { useRef, useState, useEffect } from 'react';
import { X, Printer, Send } from 'lucide-react';
import api from '../services/api';

// ── Empty shells — populated from /api/settings/company at runtime ────────────
const EMPTY_COMPANY = { name:'', address:'', city:'', country:'', phone:'', email:'', taxId:'', registrationNo:'', logoUrl:'' };
const EMPTY_BANK_US = { name:'', address:'', achRouting:'', wireRouting:'', account:'', swift:'', zelle:'' };
const EMPTY_BANK_UK = { name:'', address:'', account:'', sortCode:'', iban:'', swiftBic:'' };

// Singleton fetch so every PDFPreview instance shares one in-flight request
let _settingsCache = null;
let _settingsPromise = null;
function fetchCompanySettings() {
  if (_settingsCache) return Promise.resolve(_settingsCache);
  if (!_settingsPromise) {
    _settingsPromise = api.get('/api/settings/company')
      .then(r => { _settingsCache = r.data; return r.data; })
      .catch(() => ({ company: EMPTY_COMPANY, bank_us: EMPTY_BANK_US, bank_uk: EMPTY_BANK_UK }));
  }
  return _settingsPromise;
}

const isUkClient = (country) => {
  if (!country) return false;
  const c = country.toLowerCase().trim();
  return c === 'uk' || c === 'gb' || c.includes('united kingdom') ||
         c.includes('england') || c.includes('scotland') || c.includes('wales') || c.includes('northern ireland');
};

const PRICING_LABEL = { monthly: '(monthly)', annual: '(annual)', one_off: '(one-off)' };

const PROPOSAL_SECTIONS = [
  { key: 'user_licenses',       label: 'User Licenses',       priceHeader: 'Annual Price', pricingLabel: '(Monthly)',  bgColor: '#dbeafe', textColor: '#1e3a8a', borderColor: '#2563eb' },
  { key: 'optional_modules',    label: 'Optional Modules',    priceHeader: 'Annual Price', pricingLabel: '(Monthly)',  bgColor: '#ede9fe', textColor: '#4c1d95', borderColor: '#7c3aed' },
  { key: 'implementation_fees', label: 'Implementation Fees', priceHeader: 'One-Time Fee', pricingLabel: '(One-Off)',  bgColor: '#dcfce7', textColor: '#14532d', borderColor: '#16a34a' },
];

// ── Invoice print CSS ─────────────────────────────────────────────────────────
const INVOICE_PRINT_CSS = `
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 40px; color: #0f172a; font-size: 12px; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 20px; border-bottom: 3px solid #1e3a8a; }
  .doc-title { font-size: 36px; font-weight: 900; color: #1e3a8a; letter-spacing: -0.5px; }
  .logo-box img { height: 56px; }
  .company-addr { text-align: right; font-size: 10px; color: #475569; margin-top: 6px; line-height: 1.6; }
  .meta-row { display: flex; justify-content: space-between; margin-bottom: 32px; }
  .bill-to { font-size: 11px; }
  .bill-to .label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #94a3b8; margin-bottom: 4px; }
  .bill-to .client-name { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
  .invoice-meta { text-align: right; font-size: 11px; }
  .invoice-meta .inv-num { font-size: 18px; font-weight: 700; color: #0f172a; }
  .meta-pair { margin-top: 4px; color: #475569; }
  .meta-pair span { color: #94a3b8; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; }
  table.items { width: 100%; border-collapse: collapse; margin-bottom: 0; }
  table.items th { background: #1e3a8a; color: white; padding: 8px 12px; text-align: left; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  table.items th.num { text-align: right; }
  table.items td { padding: 9px 12px; border-bottom: 1px solid #e2e8f0; font-size: 11px; vertical-align: top; }
  table.items td.num { text-align: right; font-family: 'Courier New', monospace; }
  table.items tr:nth-child(even) td { background: #f8fafc; }
  .totals { display: flex; justify-content: flex-end; margin-top: 1px; }
  .totals-box { width: 260px; font-size: 11px; }
  .totals-row { display: flex; justify-content: space-between; padding: 5px 12px; border-bottom: 1px solid #e2e8f0; }
  .totals-row.grand { background: #1e3a8a; color: white; font-weight: 700; font-size: 13px; padding: 8px 12px; }
  .totals-row .num { font-family: 'Courier New', monospace; }
  .payment-section { margin-top: 28px; padding-top: 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: flex-start; }
  .payment-info { font-size: 10px; color: #475569; line-height: 1.8; max-width: 55%; }
  .payment-info strong { color: #0f172a; }
  .amount-due-box { border: 2px solid #1e3a8a; padding: 12px 18px; text-align: right; min-width: 200px; }
  .amount-due-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #94a3b8; }
  .amount-due-value { font-size: 24px; font-weight: 900; color: #1e3a8a; font-family: 'Courier New', monospace; }
  .footer { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; text-align: center; line-height: 1.6; }
  .due-date-box { font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 12px; }
  .due-date-box span { color: #94a3b8; font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; display: block; margin-bottom: 3px; }
  @media print { body { padding: 20px; } }
`;

// ── Proposal print CSS ────────────────────────────────────────────────────────
const PROPOSAL_PRINT_CSS = `
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { margin: 0; }
  body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 32px 32px 56px; color: #0f172a; font-size: 11px; }
  .prop-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 3px solid #1e3a8a; }
  .prop-title { font-size: 28px; font-weight: 900; color: #1e3a8a; letter-spacing: -0.5px; margin-bottom: 12px; }
  .prop-meta-grid { display: grid; grid-template-columns: 120px 1fr; gap: 4px 8px; font-size: 10px; color: #475569; }
  .prop-meta-grid .key { font-weight: 700; color: #64748b; }
  .logo-box { display: flex; flex-direction: column; align-items: flex-end; }
  .logo-box img { height: 64px; object-fit: contain; }
  .logo-box .addr { text-align: right; font-size: 9.5px; color: #475569; margin-top: 8px; line-height: 1.6; }
  .section-header { padding: 6px 10px 6px 14px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 18px; margin-bottom: 0; border-left-width: 4px; border-left-style: solid; }
  table.items { width: 100%; border-collapse: collapse; margin-bottom: 0; }
  table.items th { background: #f1f5f9; color: #334155; padding: 6px 10px; text-align: left; font-size: 9.5px; font-weight: 700; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; }
  table.items th.num { text-align: right; }
  table.items td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; font-size: 10px; vertical-align: top; color: #1e293b; }
  table.items td.num { text-align: right; font-family: 'Courier New', monospace; }
  table.items tr:nth-child(even) td { background: #f8fafc; }
  .section-subtotal { display: flex; justify-content: flex-end; padding: 5px 10px; background: #f1f5f9; font-size: 10px; font-weight: 700; color: #1e293b; border-bottom: 1px solid #cbd5e1; }
  .section-subtotal .num { font-family: 'Courier New', monospace; margin-left: 12px; }
  .grand-totals { display: flex; justify-content: flex-end; margin-top: 12px; margin-bottom: 16px; }
  .grand-box { width: 240px; font-size: 11px; border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; }
  .grand-row { display: flex; justify-content: space-between; padding: 4px 10px; border-bottom: 1px solid #e2e8f0; color: #334155; }
  .grand-row.total { background: #1e3a8a; color: white; font-weight: 700; font-size: 12px; padding: 7px 10px; border-bottom: none; }
  .grand-row .num { font-family: 'Courier New', monospace; }
  .std-notes { margin-top: 20px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-size: 9.5px; color: #334155; line-height: 1.7; }
  .std-notes .title { font-weight: 700; font-size: 10px; color: #1e3a8a; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
  .sig-block { margin-top: 20px; padding-top: 14px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #334155; }
  .sig-line { display: flex; gap: 40px; margin-top: 10px; }
  .sig-line .field { flex: 1; border-bottom: 1px solid #94a3b8; padding-bottom: 20px; font-size: 9px; color: #64748b; }
  .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 8.5px; color: #64748b; text-align: center; line-height: 1.6; }
  .print-confidential { position: fixed; bottom: 0; left: 0; right: 0; padding: 6px 32px; font-size: 9px; font-weight: 700; color: #dc2626; border-top: 1px solid #fecaca; background: white; text-align: left; }
`;

// ── Invoice payment section builder ──────────────────────────────────────────
function buildPaymentHtml(bank, isUk, currency, docDue, amount, fmt) {
  const dueLine = docDue
    ? `<div class="due-date-box"><span>Due Date</span>${new Date(docDue).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>`
    : '';
  const bankLines = isUk
    ? `<strong>No Checks Accepted.</strong> Wire / BACS Payments are preferred.<br>
       Bank: ${bank.name}, ${bank.address}<br>
       Account number: <strong>${bank.account}</strong><br>
       Sort code: <strong>${bank.sortCode}</strong><br>
       IBAN: <strong>${bank.iban}</strong><br>
       SWIFT / BIC: <strong>${bank.swiftBic}</strong><br>
       Credit cards accepted subject to an additional 4%.`
    : `Bank: ${bank.name}, ${bank.address}<br>
       ACH Routing: <strong>${bank.achRouting}</strong>&nbsp;&nbsp; Wire Routing: <strong>${bank.wireRouting}</strong><br>
       Account: <strong>${bank.account}</strong>&nbsp;&nbsp; SWIFT: <strong>${bank.swift}</strong><br>
       Payments also accepted via ZELLE to <strong>${bank.zelle}</strong><br>
       Credit cards accepted subject to an additional 4%.`;
  return `
    <div class="payment-section">
      <div class="payment-info">
        ${dueLine}
        ${bankLines}
        ${isUk ? '<div style="margin-top:8px;font-style:italic;">Subject to Reverse Charge VAT rules; customer accounts for VAT to HMRC.</div>' : ''}
      </div>
      <div class="amount-due-box">
        <div class="amount-due-label">Amount Due ${currency}</div>
        <div class="amount-due-value">${fmt(amount)}</div>
      </div>
    </div>`;
}

// ── PDFPreview Modal ──────────────────────────────────────────────────────────
export default function PDFPreview({
  type, doc, lineItems = [], client, clientName, onClose, onConfirm,
  confirmLabel = 'Confirm & Send',
}) {
  const printRef = useRef();
  const [localNotes, setLocalNotes] = useState(null);
  const [settings, setSettings]     = useState(null);

  useEffect(() => {
    fetchCompanySettings().then(s => setSettings(s));
  }, []);

  const COMPANY = settings?.company || EMPTY_COMPANY;
  const BANK_US = settings?.bank_us || EMPTY_BANK_US;
  const BANK_UK = settings?.bank_uk || EMPTY_BANK_UK;
  const EYVO_LOGO_URL = COMPANY.logoUrl || '';

  const isInvoice  = type === 'invoice';
  const isProposal = !isInvoice;
  const docNumber  = isInvoice ? doc?.invoice_number : doc?.quote_number;
  const docDate    = isInvoice ? doc?.issue_date     : doc?.quote_date;
  const docDue     = isInvoice ? doc?.due_date       : doc?.expiry_date;
  const total      = doc?.total_amount ?? 0;
  const currency   = doc?.currency_code || 'USD';
  const isUk       = isUkClient(client?.country);
  const bank       = isUk ? BANK_UK : BANK_US;
  const standardNotes = localNotes ?? doc?.standard_notes ?? '';

  // Variable substitution — replace #VarName# tokens with per-proposal or system values
  const substituteVars = (text, vars) => {
    if (!vars || !text) return text;
    return Object.entries(vars).reduce(
      (t, [k, v]) => t.replace(new RegExp(`#${k}#`, 'gi'), v), text
    );
  };
  const resolvedNotes = substituteVars(standardNotes, doc?.notes_variables);

  // ── Pricing breakdown helpers (proposal only) ─────────────────────────────
  const liMonthly = (item) => {
    const pt = item.pricing_type_snapshot || 'annual';
    const sub = item.line_subtotal_amount ?? 0;
    return pt === 'monthly' ? sub : pt === 'annual' ? sub / 12 : 0;
  };
  const liAnnual = (item) => {
    const pt = item.pricing_type_snapshot || 'annual';
    const sub = item.line_subtotal_amount ?? 0;
    return pt === 'annual' ? sub : pt === 'monthly' ? sub * 12 : 0;
  };
  const liOneOff = (item) =>
    (item.pricing_type_snapshot || 'annual') === 'one_off' ? (item.line_subtotal_amount ?? 0) : 0;
  const pdfTotalMonthly   = lineItems.reduce((s, i) => s + liMonthly(i), 0);
  const pdfTotalAnnual    = lineItems.reduce((s, i) => s + liAnnual(i),  0);
  const pdfTotalOneOff    = lineItems.reduce((s, i) => s + liOneOff(i),  0);
  const contractMonths    = doc?.contract_months || 36;
  const pdfContractTotal  =
    (pdfTotalAnnual * contractMonths / 12) + pdfTotalOneOff
    - (doc?.discount_amount || 0) + (doc?.tax_amount || 0);

  if (!doc) return null;

  const fmt = (n) =>
    new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  // ── Build PROPOSAL printable HTML ─────────────────────────────────────────
  const handlePrintProposal = () => {
    const billToLines = client
      ? [client.legal_name, client.billing_contact_name,
         client.address_line_1, client.address_line_2,
         [client.city, client.state_region, client.postal_code].filter(Boolean).join(', '),
         client.country].filter(Boolean)
      : [clientName || ''];

    // Pricing helpers for print
    const pLiMonthly = (item) => {
      const pt = item.pricing_type_snapshot || 'annual';
      const sub = item.line_subtotal_amount ?? 0;
      return pt === 'monthly' ? sub : pt === 'annual' ? sub / 12 : 0;
    };
    const pLiAnnual = (item) => {
      const pt = item.pricing_type_snapshot || 'annual';
      const sub = item.line_subtotal_amount ?? 0;
      return pt === 'annual' ? sub : pt === 'monthly' ? sub * 12 : 0;
    };
    const pLiOneOff = (item) =>
      (item.pricing_type_snapshot || 'annual') === 'one_off' ? (item.line_subtotal_amount ?? 0) : 0;
    const pTotalMonthly  = lineItems.reduce((s, i) => s + pLiMonthly(i), 0);
    const pTotalAnnual   = lineItems.reduce((s, i) => s + pLiAnnual(i),  0);
    const pTotalOneOff   = lineItems.reduce((s, i) => s + pLiOneOff(i),  0);
    const pContractMo    = doc?.contract_months || 36;
    const pContractTotal =
      (pTotalAnnual * pContractMo / 12) + pTotalOneOff
      - (doc?.discount_amount || 0) + (doc?.tax_amount || 0);

    let sectionTables = '';
    PROPOSAL_SECTIONS.forEach(section => {
      const items = lineItems.filter(i => (i.category_snapshot || 'user_licenses') === section.key);
      if (items.length === 0) return;
      const secMonthly    = items.reduce((s, i) => s + pLiMonthly(i), 0);
      const secAnnual     = items.reduce((s, i) => s + pLiAnnual(i),  0);
      const secOneOff     = items.reduce((s, i) => s + pLiOneOff(i),  0);
      const hasRecurring  = items.some(i => (i.pricing_type_snapshot || 'annual') !== 'one_off');

      const rows = items.map(item => {
        const pt      = item.pricing_type_snapshot || 'annual';
        const isOneOff = pt === 'one_off';
        const mo      = pLiMonthly(item);
        const yr      = pLiAnnual(item);
        const oo      = pLiOneOff(item);
        return `
          <tr>
            <td>${item.description_snapshot || ''}</td>
            <td class="num">${item.quantity}</td>
            <td class="num">${fmt(item.unit_price)}</td>
            <td class="num">${isOneOff ? '&mdash;' : `${currency} ${fmt(mo)}`}</td>
            <td class="num">${isOneOff
              ? `<strong>${currency} ${fmt(oo)}</strong>`
              : `${currency} ${fmt(yr)}`
            }</td>
          </tr>`;
      }).join('');

      const secParts = [];
      if (hasRecurring) {
        secParts.push(`Monthly: <strong>${currency} ${fmt(secMonthly)}/mo</strong>`);
        secParts.push(`Annual: <strong>${currency} ${fmt(secAnnual)}/yr</strong>`);
      }
      if (secOneOff > 0) {
        secParts.push(`One-off: <strong>${currency} ${fmt(secOneOff)}</strong>`);
      }

      sectionTables += `
        <div class="section-header" style="background:${section.bgColor};color:${section.textColor};border-left-color:${section.borderColor}">${section.label} <span style="font-weight:400;font-size:10px;text-transform:none;color:${section.textColor};opacity:0.75">${section.pricingLabel}</span></div>
        <table class="items">
          <thead>
            <tr>
              <th>Description</th>
              <th class="num" style="width:45px">Qty</th>
              <th class="num" style="width:90px">Unit Price</th>
              <th class="num" style="width:105px">/Month</th>
              <th class="num" style="width:105px">/Year</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="section-subtotal">${secParts.join('&nbsp;&nbsp;|&nbsp;&nbsp;')}</div>`;
    });

    const notesHtml = resolvedNotes
      ? `<div class="std-notes">
           <div class="title">Standard Notes</div>
           ${resolvedNotes.replace(/\n/g, '<br>')}
         </div>`
      : '';

    const annualTotal = pTotalAnnual + pTotalOneOff - (doc?.discount_amount || 0) + (doc?.tax_amount || 0);

    const grandRows = [
      pTotalMonthly > 0
        ? `<div class="grand-row"><span>Monthly Recurring</span><span class="num">${currency} ${fmt(pTotalMonthly)}<span style="font-size:8px;font-weight:400"> /mo</span></span></div>` : '',
      pTotalAnnual > 0
        ? `<div class="grand-row"><span>Annual Recurring</span><span class="num">${currency} ${fmt(pTotalAnnual)}<span style="font-size:8px;font-weight:400"> /yr</span></span></div>` : '',
      pTotalOneOff > 0
        ? `<div class="grand-row"><span>One-off Total</span><span class="num">${currency} ${fmt(pTotalOneOff)}</span></div>` : '',
      doc.discount_amount > 0
        ? `<div class="grand-row"><span>Discount</span><span class="num">&minus;${currency} ${fmt(doc.discount_amount)}</span></div>` : '',
      doc.tax_amount > 0
        ? `<div class="grand-row"><span>Tax</span><span class="num">${currency} ${fmt(doc.tax_amount)}</span></div>` : '',
      `<div class="grand-row total">
        <span>Total ${currency}</span>
        <span class="num">${fmt(annualTotal)}</span>
       </div>`,
    ].filter(Boolean).join('');

    const html = `<!DOCTYPE html><html><head>
      <title>Client Proposal ${docNumber || ''}</title>
      <style>${PROPOSAL_PRINT_CSS}</style></head><body>
      <div class="prop-header">
        <div>
          <div class="prop-title">Client Proposal</div>
          <div class="prop-meta-grid">
            <span class="key">Dated</span><span>${fmtDate(docDate)}</span>
            <span class="key">Proposal Ref</span><span>${docNumber || '—'}</span>
            <span class="key">Client</span><span>${client?.legal_name || clientName || '—'}</span>
            ${client?.billing_contact_name ? `<span class="key">Contact</span><span>${client.billing_contact_name}</span>` : ''}
            ${doc?.reference_number ? `<span class="key">Reference</span><span>${doc.reference_number}</span>` : ''}
            <span class="key">Valid Until</span><span>${docDue ? fmtDate(docDue) : '—'}</span>
            <span class="key">Currency</span><span>${currency}</span>
          </div>
        </div>
        <div class="logo-box">
          <img src="${EYVO_LOGO_URL}" alt="Eyvo" />
          <div class="addr">
            ${COMPANY.name}<br>
            ${COMPANY.address}<br>
            ${COMPANY.city}<br>
            ${COMPANY.phone} &bull; ${COMPANY.email}
          </div>
        </div>
      </div>

      ${sectionTables}

      <div class="grand-totals">
        <div class="grand-box" style="width:300px">${grandRows}</div>
      </div>

      ${notesHtml}

      <div class="sig-block">
        I have read, understood and agreed to the above<br>
        Signed by an authorised representative for and on behalf of:
        <strong>${client?.legal_name || clientName || '_______________'}</strong>
        <div class="sig-line">
          <div class="field">Name</div>
          <div class="field">Position</div>
          <div class="field">Signed Date &nbsp;&nbsp; E&amp;OE</div>
        </div>
      </div>

      <div class="footer">
        ${COMPANY.phone} &bull; ${COMPANY.email}<br>
        Registered in the State of Florida &bull; Tax ID ${COMPANY.taxId}<br>
        Company Registration No: ${COMPANY.registrationNo}. Registered Office: ${COMPANY.address}, ${COMPANY.city}.
      </div>
      <div class="print-confidential"><strong>Confidential</strong> &mdash; Not to be Distributed to any third party outside of ${client?.legal_name || clientName || 'client company'}</div>
    </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  // ── Build INVOICE printable HTML ──────────────────────────────────────────
  const handlePrintInvoice = () => {
    const lineRows = lineItems.map((item) => `
      <tr>
        <td>${item.description_snapshot || item.product_code_snapshot || ''}${
          item.product_code_snapshot && item.description_snapshot
            ? `<br><small style="color:#94a3b8">${item.product_code_snapshot}</small>`
            : ''
        }</td>
        <td class="num">${item.quantity}</td>
        <td class="num">${fmt(item.unit_price ?? item.unit_price_snapshot)}</td>
        <td class="num"><strong>${fmt(item.line_subtotal_amount ?? item.line_total ?? 0)}</strong></td>
      </tr>`).join('');

    const billTo = client
      ? [client.legal_name, client.billing_contact_name,
         client.address_line_1, client.address_line_2,
         [client.city, client.state_region, client.postal_code].filter(Boolean).join(' '),
         client.country].filter(Boolean).join('<br>')
      : (clientName || doc?.client_id || '');

    const html = `<!DOCTYPE html><html><head>
      <title>Invoice ${docNumber || ''}</title>
      <style>${INVOICE_PRINT_CSS}</style></head><body>
      <div class="doc-header">
        <div><div class="doc-title">INVOICE</div></div>
        <div class="logo-box">
          <img src="${EYVO_LOGO_URL}" alt="Eyvo" />
          <div class="company-addr">${COMPANY.name}<br>${COMPANY.address}<br>${COMPANY.city}<br>${COMPANY.phone} &bull; ${COMPANY.email}</div>
        </div>
      </div>
      <div class="meta-row">
        <div class="bill-to">
          <div class="label">Bill To</div>
          <div class="client-name">${client?.legal_name || clientName || '—'}</div>
          <div style="font-size:10px;color:#475569;line-height:1.7;">${billTo.replace(client?.legal_name || '', '').replace(/^<br>/, '')}</div>
        </div>
        <div class="invoice-meta">
          <div class="inv-num">${docNumber || '—'}</div>
          <div class="meta-pair"><span>Invoice Date</span> ${fmtDate(docDate)}</div>
          <div class="meta-pair"><span>Due Date</span> ${fmtDate(docDue)}</div>
          ${doc.reference_number ? `<div class="meta-pair"><span>Reference</span> ${doc.reference_number}</div>` : ''}
        </div>
      </div>
      <table class="items">
        <thead>
          <tr>
            <th>Description</th>
            <th class="num" style="width:60px">Qty</th>
            <th class="num" style="width:110px">Unit Price</th>
            <th class="num" style="width:110px">Amount ${currency}</th>
          </tr>
        </thead>
        <tbody>${lineRows}</tbody>
      </table>
      <div class="totals">
        <div class="totals-box">
          <div class="totals-row"><span>Subtotal</span><span class="num">${fmt(doc.subtotal_amount ?? total)}</span></div>
          <div class="totals-row"><span>Total Tax</span><span class="num">${fmt(doc.tax_amount ?? 0)}</span></div>
          ${doc.discount_amount > 0 ? `<div class="totals-row"><span>Discount</span><span class="num">&minus;${fmt(doc.discount_amount)}</span></div>` : ''}
          <div class="totals-row grand"><span>Total ${currency}</span><span class="num">${fmt(total)}</span></div>
        </div>
      </div>
      ${buildPaymentHtml(bank, isUk, currency, docDue, total, fmt)}
      <div class="footer">
        ${isUk ? 'Subject to Reverse Charge VAT rules; customer accounts for VAT to HMRC.<br>' : ''}
        ${COMPANY.phone} &bull; ${COMPANY.email}<br>
        Registered in the State of Florida Tax ID ${COMPANY.taxId}<br>
        Company Registration No: ${COMPANY.registrationNo}. Registered Office: ${COMPANY.address}, ${COMPANY.city}, ${COMPANY.country}.
      </div>
    </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const handlePrint = isProposal ? handlePrintProposal : handlePrintInvoice;

  // ── Preview render ─────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="pdf-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">

        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 flex-shrink-0">
          <h2 className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {isInvoice ? 'Invoice' : 'Proposal'} Preview — {docNumber}
            {isUk && <span className="ml-2 text-xs text-blue-600 font-medium">(UK — GBP / HSBC)</span>}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded hover:bg-slate-50 text-slate-600 transition-colors"
              data-testid="print-button"
            >
              <Printer size={13} /> Print / Save PDF
            </button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-400" data-testid="close-preview-button">
              <X size={17} />
            </button>
          </div>
        </div>

        {/* Document preview */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-6">
          <div
            ref={printRef}
            className="bg-white shadow-sm border border-slate-200 p-10 max-w-3xl mx-auto"
            style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13 }}
          >
            {isProposal ? (
              // ── PROPOSAL LAYOUT ────────────────────────────────────────────
              <>
                {/* Header: Title + Meta (left) | Logo (right) */}
                <div className="flex items-start justify-between mb-6 pb-5 border-b-4 border-indigo-900">
                  <div>
                    <h1 className="text-4xl font-black text-indigo-900 tracking-tight mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      Client Proposal
                    </h1>
                    <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-0.5 text-xs">
                      <span className="font-bold text-slate-500 uppercase tracking-wide">Dated</span>
                      <span className="text-slate-700">{fmtDate(docDate)}</span>
                      <span className="font-bold text-slate-500 uppercase tracking-wide">Proposal Ref</span>
                      <span className="text-slate-700 font-mono font-bold">{docNumber}</span>
                      <span className="font-bold text-slate-500 uppercase tracking-wide">Client</span>
                      <span className="text-slate-700 font-semibold">{client?.legal_name || clientName || '—'}</span>
                      {client?.billing_contact_name && (
                        <>
                          <span className="font-bold text-slate-500 uppercase tracking-wide">Contact</span>
                          <span className="text-slate-700">{client.billing_contact_name}</span>
                        </>
                      )}
                      {doc.reference_number && (
                        <>
                          <span className="font-bold text-slate-500 uppercase tracking-wide">Reference</span>
                          <span className="text-slate-700">{doc.reference_number}</span>
                        </>
                      )}
                      <span className="font-bold text-slate-500 uppercase tracking-wide">Valid Until</span>
                      <span className="text-slate-700">{docDue || '—'}</span>
                      <span className="font-bold text-slate-500 uppercase tracking-wide">Currency</span>
                      <span className="text-slate-700">{currency}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-8">
                    <img
                      src={EYVO_LOGO_URL}
                      alt="Eyvo"
                      className="h-14 object-contain mb-2 ml-auto"
                    />
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {COMPANY.name}<br />
                      {COMPANY.address}<br />
                      {COMPANY.city}<br />
                      {COMPANY.phone}<br />
                      {COMPANY.email}
                    </p>
                  </div>
                </div>

                {/* 3 Section Tables */}
                {PROPOSAL_SECTIONS.map(section => {
                  const items = lineItems.filter(i => (i.category_snapshot || 'user_licenses') === section.key);
                  if (items.length === 0) return null;
                  const secMonthly = items.reduce((s, i) => s + liMonthly(i), 0);
                  const secAnnual  = items.reduce((s, i) => s + liAnnual(i),  0);
                  const secOneOff  = items.reduce((s, i) => s + liOneOff(i),  0);
                  const hasRecurring = items.some(i => (i.pricing_type_snapshot || 'annual') !== 'one_off');
                  return (
                    <div key={section.key} className="mb-4">
                      {/* Section header bar */}
                      <div className="bg-indigo-900 text-white px-3 py-2">
                        <span className="font-bold text-xs uppercase tracking-wider">{section.label}</span>
                        <span className="text-xs font-normal text-white/70 ml-1.5">{section.pricingLabel}</span>
                      </div>
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                            <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-12">Qty</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-24">Unit Price</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide w-28">/Month</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide w-28">/Year</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, i) => {
                            const pt = item.pricing_type_snapshot || 'annual';
                            const isOneOff = pt === 'one_off';
                            const mo = liMonthly(item);
                            const yr = liAnnual(item);
                            const oo = liOneOff(item);
                            return (
                              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="py-2 px-3 border-b border-slate-100">
                                  <p className="text-sm text-slate-900">{item.description_snapshot}</p>
                                </td>
                                <td className="py-2 px-3 text-center text-sm text-slate-700 border-b border-slate-100">{item.quantity}</td>
                                <td className="py-2 px-3 text-right text-sm text-slate-700 font-mono border-b border-slate-100">{fmt(item.unit_price)}</td>
                                {/* /Month */}
                                <td className="py-2 px-3 text-right text-sm font-mono border-b border-slate-100">
                                  {isOneOff
                                    ? <span className="text-slate-300">—</span>
                                    : <span className="text-slate-900 font-semibold">{fmt(mo)}</span>
                                  }
                                </td>
                                {/* /Year */}
                                <td className="py-2 px-3 text-right text-sm font-mono border-b border-slate-100">
                                  <span className="text-slate-900 font-semibold">
                                    {isOneOff ? fmt(oo) : fmt(yr)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {/* Section subtotals */}
                      <div className="flex justify-end bg-slate-100 px-3 py-1.5 border-b-2 border-indigo-900 gap-4 text-xs flex-wrap">
                        {hasRecurring && (
                          <>
                            <span className="text-slate-500">Monthly: <span className="font-mono font-bold text-slate-700">{fmt(secMonthly)}/mo</span></span>
                            <span className="text-slate-300">|</span>
                            <span className="text-slate-500">Annual: <span className="font-mono font-bold text-slate-700">{fmt(secAnnual)}/yr</span></span>
                          </>
                        )}
                        {secOneOff > 0 && (
                          <>
                            {hasRecurring && <span className="text-slate-300">|</span>}
                            <span className="text-slate-500">One-off: <span className="font-mono font-bold text-slate-700">{fmt(secOneOff)}</span></span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Grand Totals */}
                <div className="flex justify-end mb-6">
                  <div className="w-72 space-y-1">
                    {pdfTotalMonthly > 0 && (
                      <div className="flex justify-between py-1.5 text-sm">
                        <span className="text-slate-500">Monthly Recurring</span>
                        <span className="font-mono font-medium text-slate-900">{fmt(pdfTotalMonthly)}<span className="text-slate-400 text-xs ml-1">/mo</span></span>
                      </div>
                    )}
                    {pdfTotalAnnual > 0 && (
                      <div className="flex justify-between py-1.5 text-sm border-t border-slate-100">
                        <span className="text-slate-500">Annual Recurring</span>
                        <span className="font-mono font-medium text-slate-900">{fmt(pdfTotalAnnual)}<span className="text-slate-400 text-xs ml-1">/yr</span></span>
                      </div>
                    )}
                    {pdfTotalOneOff > 0 && (
                      <div className="flex justify-between py-1.5 text-sm border-t border-slate-100">
                        <span className="text-slate-500">One-off Total</span>
                        <span className="font-mono font-medium text-slate-900">{fmt(pdfTotalOneOff)}</span>
                      </div>
                    )}
                    {doc.discount_amount > 0 && (
                      <div className="flex justify-between py-1.5 text-sm border-t border-slate-100">
                        <span className="text-slate-500">Discount</span>
                        <span className="font-mono font-medium text-red-600">−{fmt(doc.discount_amount)}</span>
                      </div>
                    )}
                    {doc.tax_amount > 0 && (
                      <div className="flex justify-between py-1.5 text-sm border-t border-slate-100">
                        <span className="text-slate-500">Tax</span>
                        <span className="font-mono font-medium text-slate-900">{fmt(doc.tax_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2.5 px-3 bg-indigo-900 text-white mt-1">
                      <span className="font-bold text-sm">Total {currency}</span>
                      <span className="font-mono font-bold text-base">{fmt(pdfTotalAnnual + pdfTotalOneOff - (doc?.discount_amount || 0) + (doc?.tax_amount || 0))}</span>
                    </div>
                  </div>
                </div>

                {/* Standard Notes — editable within modal */}
                <div className="border border-slate-200 rounded p-3 mb-5 bg-slate-50">
                  <p className="text-xs font-bold text-indigo-900 uppercase tracking-widest mb-1">Standard Notes</p>
                  <p className="text-xs text-slate-400 mb-2">Editing the raw text with <code>#Variable#</code> tokens. The printed PDF substitutes actual values.</p>
                  <textarea
                    value={standardNotes}
                    onChange={e => setLocalNotes(e.target.value)}
                    rows={9}
                    className="w-full text-xs text-slate-600 bg-transparent resize-none outline-none leading-relaxed font-mono"
                    placeholder="Standard T&Cs will appear here…"
                    data-testid="preview-standard-notes"
                  />
                </div>

                {/* Signature block */}
                <div className="border-t border-slate-200 pt-4 text-xs text-slate-500">
                  <p>I have read, understood and agreed to the above</p>
                  <p className="mt-0.5">Signed by an authorised representative for and on behalf of: <strong className="text-slate-700">{client?.legal_name || clientName || '_______________'}</strong></p>
                  <div className="flex gap-10 mt-4">
                    <div className="flex-1">
                      <div className="border-b border-slate-300 pb-5 mb-1"></div>
                      <span className="text-xs text-slate-400">Name</span>
                    </div>
                    <div className="flex-1">
                      <div className="border-b border-slate-300 pb-5 mb-1"></div>
                      <span className="text-xs text-slate-400">Position</span>
                    </div>
                    <div className="flex-1">
                      <div className="border-b border-slate-300 pb-5 mb-1"></div>
                      <span className="text-xs text-slate-400">Signed Date &nbsp; E&amp;OE</span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-5 pt-3 border-t border-slate-100 text-center text-xs text-slate-400 leading-relaxed">
                  {COMPANY.phone} · {COMPANY.email}<br />
                  Registered in the State of Florida · Tax ID {COMPANY.taxId}<br />
                  Company Registration No: {COMPANY.registrationNo}. Registered Office: {COMPANY.address}, {COMPANY.city}.<br />
                  <em>Confidential — Not for Distribution</em>
                </div>
              </>
            ) : (
              // ── INVOICE LAYOUT (unchanged) ─────────────────────────────────
              <>
                {/* Header row: INVOICE | logo + address */}
                <div className="flex items-start justify-between mb-8 pb-6 border-b-4 border-indigo-900">
                  <div>
                    <h1 className="text-5xl font-black text-indigo-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      INVOICE
                    </h1>
                  </div>
                  <div className="text-right">
                    <img src={EYVO_LOGO_URL} alt="Eyvo" className="h-12 object-contain mb-2 ml-auto" />
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {COMPANY.name}<br />
                      {COMPANY.address}<br />
                      {COMPANY.city}<br />
                      {COMPANY.country}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {COMPANY.phone} · {COMPANY.email}
                    </p>
                  </div>
                </div>

                {/* Bill To + Invoice Meta */}
                <div className="flex items-start justify-between mb-7">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Bill To</p>
                    <p className="font-bold text-slate-900 text-sm">{client?.legal_name || clientName || '—'}</p>
                    {client?.billing_contact_name && <p className="text-xs text-slate-500 mt-0.5">Attn: {client.billing_contact_name}</p>}
                    {client?.address_line_1 && <p className="text-xs text-slate-500">{client.address_line_1}</p>}
                    {client?.address_line_2 && <p className="text-xs text-slate-500">{client.address_line_2}</p>}
                    {(client?.city || client?.postal_code) && (
                      <p className="text-xs text-slate-500">
                        {[client.city, client.state_region, client.postal_code].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {client?.country && <p className="text-xs text-slate-500">{client.country}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{docNumber}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      <span className="text-slate-400 uppercase text-xs font-semibold tracking-wide">Invoice Date: </span>
                      {fmtDate(docDate)}
                    </p>
                    <p className="text-xs text-slate-500">
                      <span className="text-slate-400 uppercase text-xs font-semibold tracking-wide">Due Date: </span>
                      {fmtDate(docDue)}
                    </p>
                    {doc.reference_number && (
                      <p className="text-xs text-slate-500">
                        <span className="text-slate-400 uppercase text-xs font-semibold tracking-wide">Reference: </span>
                        {doc.reference_number}
                      </p>
                    )}
                    <div className="mt-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${
                        doc.status === 'paid'    ? 'bg-green-100 text-green-700' :
                        doc.status === 'sent'    ? 'bg-blue-100 text-blue-700'   :
                        doc.status === 'overdue' ? 'bg-red-100 text-red-700'     :
                        'bg-slate-100 text-slate-600'
                      }`}>{doc.status}</span>
                    </div>
                  </div>
                </div>

                {/* Line Items Table */}
                <table className="w-full mb-0.5">
                  <thead>
                    <tr className="bg-indigo-900 text-white">
                      <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wide">Description</th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold uppercase tracking-wide w-14">Qty</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wide w-28">Unit Price</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wide w-28">Amount {currency}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-4 px-3 text-xs text-slate-400 text-center">No line items</td>
                      </tr>
                    ) : lineItems.map((item, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="py-2.5 px-3 border-b border-slate-100">
                          <p className="text-sm text-slate-900">{item.description_snapshot || item.product_code_snapshot}</p>
                          {item.product_code_snapshot && item.description_snapshot && (
                            <p className="text-xs text-slate-400">{item.product_code_snapshot}</p>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-center text-sm text-slate-700 border-b border-slate-100">{item.quantity}</td>
                        <td className="py-2.5 px-3 text-right text-sm text-slate-700 font-mono border-b border-slate-100">{fmt(item.unit_price ?? item.unit_price_snapshot)}</td>
                        <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-900 font-mono border-b border-slate-100">{fmt(item.line_subtotal_amount ?? item.line_total ?? 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mb-8 border-b border-slate-200 pb-4">
                  <div className="w-64">
                    <div className="flex justify-between py-2 text-sm">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="font-mono font-medium text-slate-900">{fmt(doc.subtotal_amount ?? total)}</span>
                    </div>
                    <div className="flex justify-between py-2 text-sm border-t border-slate-100">
                      <span className="text-slate-500">Total Tax</span>
                      <span className="font-mono font-medium text-slate-900">{fmt(doc.tax_amount ?? 0)}</span>
                    </div>
                    {doc.discount_amount > 0 && (
                      <div className="flex justify-between py-2 text-sm border-t border-slate-100">
                        <span className="text-slate-500">Discount</span>
                        <span className="font-mono font-medium text-red-600">−{fmt(doc.discount_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2.5 px-3 bg-indigo-900 text-white mt-1">
                      <span className="font-bold text-sm">Total {currency}</span>
                      <span className="font-mono font-bold text-base">{fmt(total)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {doc.notes_external && (
                  <div className="mb-5 p-3 bg-slate-50 rounded border border-slate-200">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Notes</p>
                    <p className="text-sm text-slate-700 whitespace-pre-line">{doc.notes_external}</p>
                  </div>
                )}

                {/* Payment Section */}
                <div className="flex items-start justify-between border-t border-slate-200 pt-5">
                  <div className="text-xs text-slate-500 leading-relaxed max-w-md">
                    {docDue && (
                      <div className="mb-3">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">Due Date</p>
                        <p className="text-sm font-bold text-slate-900">{fmtDate(docDue)}</p>
                      </div>
                    )}
                    {isUk ? (
                      <>
                        <p className="font-semibold text-slate-700 mb-1">No Checks Accepted. Wire / BACS Payments are preferred.</p>
                        <p>Bank: <strong className="text-slate-700">{bank.name}</strong>, {bank.address}</p>
                        <p>Account number: <strong className="text-slate-700 font-mono">{bank.account}</strong></p>
                        <p>Sort code: <strong className="text-slate-700 font-mono">{bank.sortCode}</strong></p>
                        <p>IBAN: <strong className="text-slate-700 font-mono">{bank.iban}</strong></p>
                        <p>SWIFT / BIC: <strong className="text-slate-700 font-mono">{bank.swiftBic}</strong></p>
                        <p className="mt-1.5 text-slate-500">Credit cards accepted subject to an additional 4%.</p>
                      </>
                    ) : (
                      <>
                        <p>Bank: <strong className="text-slate-700">{bank.name}</strong>, {bank.address}</p>
                        <p>ACH Routing: <strong className="text-slate-700 font-mono">{bank.achRouting}</strong></p>
                        <p>Wire Routing: <strong className="text-slate-700 font-mono">{bank.wireRouting}</strong></p>
                        <p>Account: <strong className="text-slate-700 font-mono">{bank.account}</strong> · SWIFT: <strong className="text-slate-700 font-mono">{bank.swift}</strong></p>
                        <p className="mt-1">Payments via ZELLE to <strong className="text-slate-700">{bank.zelle}</strong></p>
                        <p className="mt-1.5 text-slate-500">Credit cards accepted subject to an additional 4%.</p>
                      </>
                    )}
                    {isUk && <p className="mt-3 text-slate-600 italic">Subject to Reverse Charge VAT rules; customer accounts for VAT to HMRC.</p>}
                  </div>
                  <div className="border-2 border-indigo-900 px-5 py-3 text-right flex-shrink-0 ml-6">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">Amount Due {currency}</p>
                    <p className="text-2xl font-black text-indigo-900 font-mono">{fmt(total)}</p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 text-center text-xs text-slate-400 leading-relaxed">
                  {COMPANY.phone} · {COMPANY.email}<br />
                  Registered in the State of Florida Tax ID {COMPANY.taxId}<br />
                  Company Registration No: {COMPANY.registrationNo}. Registered Office: {COMPANY.address}, {COMPANY.city}, {COMPANY.country}.
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action Footer */}
        <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between rounded-b-xl flex-shrink-0">
          <p className="text-xs text-slate-500">
            {isProposal
              ? 'Proposal PDF — 3-section layout with Eyvo logo'
              : isUk ? 'UK client — HSBC GBP bank details.' : 'US/International — Bank of America USD.'}
          </p>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded-md hover:bg-white text-slate-600 transition-colors">
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-900 text-white text-sm font-semibold rounded-md hover:bg-indigo-800 transition-colors"
              data-testid="confirm-send-button"
            >
              <Send size={14} />
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
